import { useLocation, useSearchParams } from 'react-router-dom';
import { useCalendarEvents } from '../../hooks/useCalendar';
import { addDaysKey, isDayKey, rangeFor } from '../../lib/calendar/zonedCalendar';

/**
 * Resolve an aggregated calendar event for the full view and the editor.
 *
 * Aggregated ids (gcal_…, oc_…, crm_…, book_…, bl_…) have no single-event GET,
 * and this epic adds none. Navigation from the grid carries the event in router
 * state; a cold load or reload reads `?day=` and finds the event in that day's
 * aggregated window instead. Always fetched, so a stale router copy is replaced
 * by the current one as soon as the day loads.
 *
 * `matchBookingTwin` (#791 E30, opt-in; only CalendarEventPage passes it) makes a
 * `bl_<booking_id>` id also find its booking when a Google/Outlook twin absorbed
 * the chip (the aggregator then lists it under the provider's id, with
 * `booking_id` set), and tolerates `?day=` being one day off: the decline bell
 * writes the day in the host's profile zone, this page reads it in the display
 * zone, which can differ by a day. That is ONE request over day-1 .. day+1. The
 * editor never passes it, so `/calendar/event/bl_x/edit` keeps exact-id matching
 * and never opens the provider twin under a `bl_` URL; every other id keeps the
 * one-day window.
 *
 * @returns {{ event: object|null, isLoading: boolean, notFound: boolean, dayKey: string|null }}
 */
export function useCalendarEventLookup(eventId, displayZone, { matchBookingTwin = false } = {}) {
    const location = useLocation();
    const [params] = useSearchParams();
    const dayKey = isDayKey(params.get('day')) ? params.get('day') : null;
    // No id (the create route) never matches router state.
    const stateEvent = eventId && location.state?.event?.event_id === eventId ? location.state.event : null;

    const bookingId = matchBookingTwin && typeof eventId === 'string' && eventId.startsWith('bl_') ? eventId.slice(3) : '';
    const firstDay = dayKey && bookingId ? addDaysKey(dayKey, -1) : dayKey || '1970-01-01';
    const lastDay = dayKey && bookingId ? addDaysKey(dayKey, 1) : dayKey || '1970-01-01';
    const { fromIso } = rangeFor('day', firstDay, displayZone);
    const { toIso } = rangeFor('day', lastDay, displayZone);
    const query = useCalendarEvents(fromIso, toIso, { enabled: Boolean(dayKey) });
    const listed = dayKey ? (query.data?.events || []) : [];
    const fetched = dayKey
        ? listed.find((e) => e.event_id === eventId) || (bookingId ? listed.find((e) => e.booking_id === bookingId) : undefined)
        : null;

    const event = fetched || stateEvent || null;
    const isLoading = !event && Boolean(dayKey) && query.isLoading;
    return { event, isLoading, notFound: !event && !isLoading, dayKey };
}
